import type {
  ReploElementType,
  ReploPartialElement,
} from "schemas/generated/element";

import * as React from "react";

import { addListener } from "@reduxjs/toolkit";
import { skipToken } from "@tanstack/react-query";
import { useElementEditorErrorContext } from "@v1/contexts/ElementEditorErrorContext";
import {
  handleUpdateComponentError,
  selectIsShopifyIntegrationEnabled,
} from "@v1/reducers/core-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { shopifyRouter } from "@v1/trpc/routes/shopify";
import { validateElement } from "@v1/utils/element";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { isPageOrShopifyArticleType } from "schemas/element";

/**
 * This hook returns a function that validates an element.
 */
export function useElementValidation(elementType?: ReploElementType) {
  const dispatch = useEditorDispatch();
  const { setErrors } = useElementEditorErrorContext();
  const projectId = useCurrentProjectId();
  const isShopifyIntegrationEnabled = useEditorSelector(
    selectIsShopifyIntegrationEnabled,
  );
  const isPageOrShopifyArticle = isPageOrShopifyArticleType(elementType);

  const { data: shopifyBlogs, isError } = shopifyRouter.getBlogs.useQuery(
    isPageOrShopifyArticle && projectId && isShopifyIntegrationEnabled
      ? projectId
      : skipToken,
  );

  // NOTE (Sebas 2023-11-27): This is in charge of setting the
  // "pathAlreadyExists" error when the API fails because of that.
  React.useEffect(() => {
    const unsubscribe = dispatch(
      addListener({
        actionCreator: handleUpdateComponentError,
        effect: (action) => {
          const { error } = action.payload;
          const key = error?.data?.userFacingDetails?.key;
          if (typeof key === "string" && key.startsWith("pathAlreadyInUse")) {
            setErrors("path", ["pathAlreadyExists"]);
          }
        },
      }),
    );
    return unsubscribe as unknown as () => void;
  }, [dispatch, setErrors]);

  return React.useCallback(
    (element: ReploPartialElement) => {
      return validateElement(element, {
        storeHasBlogs: shopifyBlogs && shopifyBlogs.length > 0,
        isErrorFetchingData: isError,
      });
    },
    [shopifyBlogs, isError],
  );
}

/**
 * This hook returns the Shopify blogs for the current project.
 */
export function useShopifyBlogs(elementType: ReploElementType) {
  const isPageOrShopifyArticle = isPageOrShopifyArticleType(elementType);
  const projectId = useCurrentProjectId();
  const isShopifyIntegrationEnabled = useEditorSelector(
    selectIsShopifyIntegrationEnabled,
  );
  const {
    data: shopifyBlogs,
    isError,
    isLoading,
  } = shopifyRouter.getBlogs.useQuery(
    isPageOrShopifyArticle && projectId && isShopifyIntegrationEnabled
      ? projectId
      : skipToken,
  );

  return {
    shopifyBlogs,
    isLoading,
    isError,
  };
}
